import { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  FormInputText,
  InputLabel,
  Select,
  Text,
} from '@hopskipdrive/react-components';
import { colorTokens } from '@rideiq/shared/components/variables';

import { useRiderValidation } from '../../../../hooks/useRiderValidation';
import { useToastStore } from '../../../../shared/stores/toastStore';

import { Divider, FlexRow, PhoneNumberInput } from './CaregiverForm.styles';
import { contactSchema } from './validationSchema';

export interface ICaregiverFormProps {
  onSubmit: (data) => void;
  onCancel: () => void;
  contact?: RidersContactForm;
  setIsSubmitting: (isSubmitting: boolean) => void;
}

export const CaregiverForm: FC<ICaregiverFormProps> = ({ contact, onSubmit, setIsSubmitting }) => {
  const { validateRiderDetails, isFetching } = useRiderValidation();
  const { addToast } = useToastStore();
  const [fullNameError, setFullNameError] = useState('');
  const {
    control,
    handleSubmit,
    setError,
    formState: { isDirty, errors },
  } = useForm<RidersContactForm>({
    defaultValues: useMemo(() => {
      return {
        firstName: contact?.firstName,
        lastName: contact?.lastName,
        role: 'caregiver',
        relationship: contact?.relationship,
        phoneNumber: contact?.phoneNumber,
        email: contact?.email,
      };
    }, [contact]),
    resolver: yupResolver(contactSchema()),
  });

  const handleValidation = async (data) => {
    if (!isDirty) {
      onSubmit(data);

      return;
    }
    setIsSubmitting(true);
    const abortController = new AbortController();

    setFullNameError('');

    const response = await validateRiderDetails({ passengersContactsAttributes: [data] });

    if (abortController.signal.aborted) {
      return;
    }
    if (response.type === 'error') {
      const careGiverError = response.data?.errors?.passengerContact;

      for (const property in careGiverError) {
        switch (property) {
          case 'role':
            setError('role', { message: careGiverError.role });
            break;
          case 'relationship':
            setError('relationship', { message: careGiverError.relationship });
            break;
          case 'email':
            setError('email', { message: careGiverError.email });
            break;
          case 'fullName':
            setFullNameError(careGiverError?.fullName);
            setError('firstName', { message: careGiverError?.fullName });
            setError('lastName', { message: careGiverError?.fullName });
            break;
          default:
            addToast({
              severity: 'error',
              message:
                typeof careGiverError[property] === 'string'
                  ? careGiverError[property]
                  : `Incorrect input for ${property}`,
            });
            break;
        }
      }

      setIsSubmitting(false);
    } else {
      onSubmit(data);
    }
    setIsSubmitting(false);
  };

  return (
    <form
      id="caregiver-form"
      aria-label="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit((data) => !isFetching && handleValidation(data))();
      }}
    >
      <FlexRow>
        <Box display="flex" alignItems="center">
          <InputLabel fieldtype="required" htmlFor="firstName">
            First Name
          </InputLabel>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { onChange, value, name } }) => (
              <FormInputText
                id={name}
                variant="outlined"
                name={name}
                value={value}
                data-testid="firstName"
                fullWidth
                error={fullNameError ? !!(errors.firstName && errors.lastName) : !!errors.firstName}
                helperText={fullNameError ? '' : errors.firstName?.message}
                onChange={onChange}
              />
            )}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <InputLabel fieldtype="required" htmlFor="lastName">
            Last Name
          </InputLabel>
          <Controller
            control={control}
            name="lastName"
            render={({ field: { onChange, value, name } }) => (
              <FormInputText
                id={name}
                variant="outlined"
                name={name}
                value={value}
                data-testid="lastName"
                fullWidth
                error={fullNameError ? !!(errors.lastName && errors.firstName) : !!errors.lastName}
                helperText={fullNameError ? '' : errors.lastName?.message}
                onChange={onChange}
              />
            )}
          />
        </Box>
      </FlexRow>
      <FlexRow style={{ justifyContent: 'center' }}>
        {fullNameError && (
          <Text style={{ fontSize: '12px', lineHeight: 'normal', fontWeight: 300, color: colorTokens.error.dark }}>
            {fullNameError}
          </Text>
        )}
      </FlexRow>
      <FlexRow>
        <Box display="flex" alignItems="center">
          <InputLabel>Role</InputLabel>
          <Text data-testid="contactRole" style={{ fontWeight: 700, flex: 1 }}>
            Caregiver
          </Text>
        </Box>

        <Box display="flex" alignItems={'center'}>
          <InputLabel htmlFor="relationship">Relationship</InputLabel>
          <Controller
            control={control}
            defaultValue={contact?.relationship || ''}
            name="relationship"
            render={({ field: { value, onChange } }) => (
              <Select
                id="relationship"
                options={[
                  { label: 'Parent or Guardian', value: 'guardian' },
                  { label: 'Relative', value: 'relative' },
                  { label: 'Social Worker', value: 'social_worker' },
                  { label: 'Educational Specialist', value: 'ed_specialist' },
                  { label: 'Other', value: 'other' },
                ]}
                value={value}
                setValue={onChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    data-testid="relationship"
                    fullWidth
                    error={!!errors.relationship}
                    helperText={errors.relationship?.message}
                  />
                )}
              />
            )}
          />
        </Box>
      </FlexRow>
      <Divider />
      <FlexRow>
        <Box display="flex" alignItems="center">
          <InputLabel fieldtype="required" htmlFor="phoneNumber">
            Phone Number
          </InputLabel>

          <Controller
            control={control}
            name="phoneNumber"
            defaultValue={contact?.phoneNumber || ''}
            render={({ field: { onChange, value, name } }) => (
              <PhoneNumberInput
                variant="outlined"
                name={name}
                id={name}
                data-testid="phoneNumber"
                onValueChange={(value) => onChange(value)}
                initialValue={value}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber ? errors.phoneNumber.message : ''}
              />
            )}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <InputLabel htmlFor="email">Email</InputLabel>
          <Controller
            control={control}
            name="email"
            defaultValue={contact?.email || ''}
            render={({ field: { onChange, value, name } }) => (
              <FormInputText
                id={name}
                placeholder=""
                name={name}
                data-testid="email"
                variant="outlined"
                fullWidth
                onChange={onChange}
                value={value}
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ''}
              />
            )}
          />
        </Box>
      </FlexRow>
    </form>
  );
};
